import { Client } from '@microsoft/microsoft-graph-client';
import { AuthenticationProvider } from '@microsoft/microsoft-graph-client';

// Cache global para el cliente autenticado
let cachedGraphClient: Client | null = null;
let authPromise: Promise<Client | null> | null = null;
let lastAuthTime = 0;
const AUTH_CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

// Crear cliente Graph con token reutilizado
const createGraphClient = async (accessToken: string): Promise<Client | null> => {
  const now = Date.now();
  
  // Si tenemos cliente cacheado y no ha expirado, reutilizarlo
  if (cachedGraphClient && (now - lastAuthTime < AUTH_CACHE_DURATION)) {
    console.log('📋 Reutilizando cliente Graph cacheado');
    return cachedGraphClient;
  }
  
  // Si ya hay una promesa de autenticación en curso, esperarla
  if (authPromise) {
    console.log('⏳ Esperando autenticación Graph en curso...');
    return authPromise;
  }
  
  console.log('🔐 Creando nuevo cliente Graph...');
  
  authPromise = new Promise(async (resolve) => {
    try {
      const authProvider: AuthenticationProvider = {
        getAccessToken: async () => accessToken
      };

      const client = Client.initWithMiddleware({
        authProvider: authProvider,
      });
      
      cachedGraphClient = client;
      lastAuthTime = now;
      
      console.log('✅ Cliente Graph creado exitosamente');
      resolve(client);
    } catch (error) {
      console.error('❌ Error creando cliente Graph:', error);
      resolve(null);
    } finally {
      authPromise = null;
    }
  });
  
  return authPromise;
};

// Cache de fotos para evitar requests repetidos
const photoCache = new Map<string, string>();

export const loadUserPhotos = async (
  emails: string[], 
  accessToken: string,
  onProgress?: (loaded: number, total: number) => void
): Promise<Record<string, string>> => {
  console.log(`🚀 Iniciando carga de fotos para ${emails.length} usuarios`);
  
  const photos: Record<string, string> = {};
  
  // Filtrar emails ya cacheados
  const emailsToLoad = emails.filter(email => {
    if (photoCache.has(email)) {
      photos[email] = photoCache.get(email)!;
      return false;
    }
    return true;
  });
  
  if (emailsToLoad.length === 0) {
    console.log('📋 Todas las fotos ya están en cache');
    return photos;
  }
  
  console.log(`📊 Cargando ${emailsToLoad.length} fotos nuevas (${emails.length - emailsToLoad.length} en cache)`);
  
  // Crear cliente una sola vez
  const graphClient = await createGraphClient(accessToken);
  if (!graphClient) {
    console.error('❌ No se pudo crear cliente Graph');
    return photos;
  }
  
  let loaded = 0;
  
  // Procesar en lotes pequeños secuencialmente (sin Promise.all para evitar spam)
  const BATCH_SIZE = 2;
  for (let i = 0; i < emailsToLoad.length; i += BATCH_SIZE) {
    const batch = emailsToLoad.slice(i, i + BATCH_SIZE);
    
    for (const email of batch) {
      try {
        // Buscar usuario por email
        const users = await graphClient
          .api('/users')
          .filter(`mail eq '${email}' or userPrincipalName eq '${email}'`)
          .select('id,displayName')
          .get();

        if (users.value && users.value.length > 0) {
          const user = users.value[0];
          
          try {
            // Obtener foto del usuario
            const photo = await graphClient
              .api(`/users/${user.id}/photo/$value`)
              .get();
            
            // Convertir blob a base64
            const reader = new FileReader();
            const photoBase64 = await new Promise<string>((resolve) => {
              reader.onloadend = () => resolve(reader.result as string);
              reader.readAsDataURL(photo);
            });
            
            photos[email] = photoBase64;
            photoCache.set(email, photoBase64);
            
            console.log(`✅ Foto cargada: ${user.displayName}`);
          } catch (photoError) {
            console.log(`📷 Sin foto para: ${email}`);
            // No hay foto, pero no es error
          }
        } else {
          console.log(`👤 Usuario no encontrado: ${email}`);
        }
      } catch (error) {
        console.error(`❌ Error procesando ${email}:`, error);
      }
      
      loaded++;
      onProgress?.(loaded, emailsToLoad.length);
    }
    
    // Pausa entre lotes para no saturar
    if (i + BATCH_SIZE < emailsToLoad.length) {
      await new Promise(resolve => setTimeout(resolve, 200));
    }
  }
  
  console.log(`🎉 Carga completada: ${Object.keys(photos).length} fotos obtenidas`);
  return photos;
};

// Limpiar cache (opcional)
export const clearPhotoCache = () => {
  photoCache.clear();
  cachedGraphClient = null;
  lastAuthTime = 0;
  console.log('🧹 Cache de fotos limpiado');
};